// Admin — Device by Unique Number.
//
// Replaces the old Windows "Devices" form for the one job that matters day to
// day: look a device up, and if it is not assigned, attach it to a user, a
// location and a room.
//
// The old form put ~60 fields on one screen and left the operator to work out
// which mattered. This shows what the device IS, then walks the assignment in
// the order the decisions actually depend on each other:
//
//     Master User  ->  Location (theirs, or new)  ->  Room (theirs, new, or common)
//
// Each step is disabled until the one before it is answered, because a location
// list means nothing before a user is chosen and a room list means nothing
// before a location is.
//
// Super Admin only — it writes ownership.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Divider, Chip, Alert, Tooltip,
  CircularProgress, Autocomplete, Table, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { getOtaBinFiles } from '../../../core/api/api';
import { mapDeviceToBinFile, servedOtaUrl, knownBuildSha } from '../../../core/devices/otaBinFiles';
import { askVersion, matchesBuild } from '../../../core/devices/firmwareVersion';
import { parseServerTime } from '../../../core/devices/firmwareInfo';
import {
  setPendingCommand, PENDING_UPDATE_COMMAND, isUpdateCommand,
} from '../../../core/devices/firmwareUpdate';
import {
  startOtaAndWatch, otaOutcome, otaPayload, OTA_ACK, OTA_SILENCE_MS,
} from '../../../core/devices/otaProgress';
import { useApp } from '../../../core/state/AppContext';
import {
  findDevice, getAllUsers, userLabel, locationsOf, roomsOf, devicesIn,
  createLocation, createRoom, assignDevice, unassignDeviceFully,
  setDeviceType as saveDeviceType,
  deviceUserIds, grantUserAccess, revokeUserAccess,
  COMMON_ROOM_ID,
} from '../../../core/devices/deviceAdmin';
import { getDeviceTypes, renameRoom } from '../../../core/api/api';
import { tokenFilter } from '../../../core/utils/search';
import DeviceWifiPanel from '../components/DeviceWifiPanel';

const NEW = '__new__';

// A dropdown you can type into.
//
// Every list on this page can be long — 450+ users, and a location with dozens
// of rooms or devices — so a plain Select means scrolling to find something you
// already know the name of. Autocomplete filters as you type and still behaves
// as a picker (no free text: the value must be one of the options).
//
// ⚠️ VALUES ARE COMPARED WITH String(), NOT ===, AND '' IS THE ONLY "UNSET".
// Room 0 is a legitimate choice (Common devices) and is falsy, so the usual
// `value || null` and `if (!value)` shortcuts would silently unselect it.
function Picker({
  label, value, onChange, options, disabled, width, helperText, autoFocusText,
}) {
  const selected = options.find((o) => String(o.id) === String(value)) ?? null;
  return (
    <Autocomplete
      size="small"
      sx={{ width }}
      options={options}
      value={value === '' || value == null ? null : selected}
      onChange={(_, o) => onChange(o ? o.id : '')}
      disabled={disabled}
      getOptionLabel={(o) => o.label ?? ''}
      // Word-order-independent, and searching the SUB line too — so a user is
      // found by mobile number and a device by its unique number, not just by
      // the name on the first line.
      filterOptions={tokenFilter((o) => `${o.label ?? ''} ${o.sub ?? ''}`)}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      renderOption={(props, o) => (
        <li {...props} key={String(o.id)}>
          <Box>
            <Typography variant="body2" fontWeight={600}>{o.label}</Typography>
            {o.sub && (
              <Typography variant="caption" color="text.secondary">{o.sub}</Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(p) => (
        <TextField {...p} label={label} helperText={helperText} autoFocus={autoFocusText} />
      )}
    />
  );
}

// A server timestamp, readable.
//
// ⚠️ THROUGH parseServerTime, ALWAYS. The backend writes IST wall-clock and
// labels it `…Z`, so printing the raw string — which this page used to do — is
// off by five and a half hours and reads as a device that has not been seen
// since this evening when it answered a minute ago.
//
// The relative part is the one that gets read; the absolute date is what gets
// quoted back on the phone, so both are shown.
function When({ raw }) {
  const ms = parseServerTime(raw);
  if (ms == null) return '—';
  const mins = Math.round((Date.now() - ms) / 60000);
  const rel = mins < 1 ? 'just now'
    : mins < 60 ? `${mins}m ago`
      : mins < 2880 ? `${Math.floor(mins / 60)}h ago`
        : `${Math.floor(mins / 1440)}d ago`;
  return (
    <>
      {new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>
        {rel}
      </Box>
    </>
  );
}

// Option builders — one place, so the browse and assign sides cannot drift.
const userOptions = (users) => users.map((u) => ({
  id: u.UserId, label: userLabel(u), sub: `${u.MobileNumber || ''} · ID ${u.UserId}`, raw: u,
}));
const locationOptions = (locs) => locs.map((l) => ({
  id: l.LocationID, label: l.LocationName || `Location ${l.LocationID}`,
}));
const roomOptions = (rooms) => rooms.map((r) => ({
  id: r.RoomID, label: r.RoomName || `Room ${r.RoomID}`,
}));
const deviceOptions = (devs) => devs.map((dev) => ({
  id: dev.UniqueNumber,
  label: dev.DeviceName || 'Unnamed',
  sub: `${dev.UniqueNumber}${dev.DeviceTypeName ? ` · ${dev.DeviceTypeName}` : ''}`,
}));

export default function DeviceAdminScreen() {
  const { isSuperAdmin, user: me, userId } = useApp();

  const [uno, setUno] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);   // { found, device, assigned }
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [newRoom, setNewRoom] = useState('');
  const [deviceName, setDeviceName] = useState('');

  // ── Browse panel: user -> location -> room -> device ──────────────────────
  // Kept separate from the ASSIGN selects below. They look alike but mean
  // opposite things: browsing asks "where is this device now", assigning says
  // "where should it go". Sharing state would make picking a device to inspect
  // silently rewrite the destination.
  const [bUser, setBUser] = useState(null);
  const [bLocations, setBLocations] = useState([]);
  const [bLocationId, setBLocationId] = useState('');
  const [bRooms, setBRooms] = useState([]);
  const [bRoomId, setBRoomId] = useState('');
  const [bDevices, setBDevices] = useState([]);
  // The Device select used to be write-only (`value=""`): a trigger, not a
  // selection. Now that it defaults to the first device it has to SHOW which
  // one, or the page fills with a device the row does not name.
  const [bDeviceUno, setBDeviceUno] = useState('');
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [deviceType, setDeviceType] = useState('');

  // Unassign is destructive and irreversible from here, so it asks for the
  // signed-in admin's own password — the same guard the old form's operators
  // expect, and enough to stop a mis-click emptying a live installation.
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');

  // What the device already has, held until its list has loaded. The cascades
  // below CLEAR the level under them whenever their parent changes, so a
  // preselect written straight after search() is wiped a tick later — it has to
  // be applied when the options actually arrive.
  const pending = useRef(null);   // { locationId, roomId }

  // Latest pickDevice, for the browse cascade to call. Declared here because
  // the effect that uses it is written above the function it holds.
  const pickRef = useRef(() => {});

  // ⚠️ THE CASCADE MUST RE-RUN ON EVERY LOOKUP, INCLUDING ONE FOR THE SAME OWNER.
  //
  // `setUser(users.find(...))` hands React the SAME object it is already holding
  // when two devices share a master user, so the state does not change, the two
  // effects below never fire, and Location / Room keep the PREVIOUS device's
  // answer while the facts table beside them shows this one's — the same screen
  // stating two different rooms. It is not a display fault either: Save writes
  // whatever those selects hold, so the stale pair would have been written back
  // and the device actually moved.
  //
  // This counter is bumped by every successful lookup and is a dependency of
  // both cascades, so they always re-run and always re-apply `pending`.
  const [preselect, setPreselect] = useState(0);

  // Set when the device's own location or room is NOT in the owner's list, so a
  // blank select is explained rather than read as "this device has no room".
  const [preselectMiss, setPreselectMiss] = useState('');   // '' | 'location' | 'room'

  // Users who can see this device.
  // ── Firmware, independent of the assignment ─────────────────────────────
  // Its preconditions are different (no owner, no location needed) and so is
  // its table, so it has its own state and its own save.
  const [bins, setBins] = useState([]);
  const [binPick, setBinPick] = useState('');
  const [servedNow, setServedNow] = useState('');
  const [pendingCmd, setPendingCmd] = useState('');
  const [fwBusy, setFwBusy] = useState(false);
  const [fwMsg, setFwMsg] = useState(null);      // { severity, text }
  const [otaLines, setOtaLines] = useState([]);
  const otaStop = useRef(null);

  useEffect(() => {
    getOtaBinFiles()
      // Row 17 carries an empty BinFileName; as an option it reads as blank and
      // would un-point the device.
      .then((res) => setBins((Array.isArray(res.data) ? res.data : [])
        .filter((r) => String(r.BinFileName || '').trim())))
      .catch(() => setBins([]));
  }, []);

  // Stop listening when the page goes away — the OTA watch holds a subscription.
  useEffect(() => () => { if (otaStop.current) otaStop.current(); }, []);

  const [access, setAccess] = useState([]);      // UserId[]
  const [addUserId, setAddUserId] = useState('');
  const [accessBusy, setAccessBusy] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    getDeviceTypes()
      .then((res) => setTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTypes([]));
    getAllUsers()
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]));
  }, [isSuperAdmin]);

  // Takes the number EXPLICITLY when called from the browse panel: setUno()
  // does not update the value this closure captured, so searching "the current
  // box" straight after selecting a device would look up the previous one.
  //
  // ⚠️ `{ refresh: true }` FOR THE RE-READ AFTER AN ACTION, AND IT IS NOT
  // COSMETIC. Every action here ends `setSaved(…)` / `setFwMsg(…)` and then
  // `await search()` to pick the new row up — and search's own reset then wiped
  // the message the action had just written, one tick later. "Assigned to X",
  // "Confirmed — the device will download …" and "Parked …" were all being
  // cleared by the very refresh meant to confirm them, so the operator saw a
  // button do nothing. A refresh also keeps `result` and the OTA log, because
  // blanking the panel it is re-reading is a flicker with no purpose.
  const search = useCallback(async (value, opts) => {
    const target = String(value ?? uno).trim();
    if (!target) return;
    const refresh = !!(opts && opts.refresh);
    setSearching(true);
    if (!refresh) {
      setError('');
      setSaved('');
      setResult(null);
    }
    try {
      const r = await findDevice(target);
      setResult(r);
      if (r.found) {
        setDeviceName(r.device.DeviceName || '');
        setDeviceType(r.device.DeviceID ?? '');
        // Start the edit from the TRUTH, not from blank: the panel should show
        // where the device is now so a change is a change, not a re-entry.
        pending.current = {
          locationId: r.device.LocationID ?? r.device.LocationId ?? '',
          roomId: r.device.RoomId ?? '',
        };
        const owner = users.find((u) => String(u.UserId) === String(r.device.MasterUserId));
        setUser(owner || null);
        // ⚠️ AFTER setUser AND UNCONDITIONALLY — see the note on `preselect`.
        // Same owner means setUser is a no-op, and this is then the only thing
        // that makes the Location / Room cascade look at the new device.
        setPreselect((n) => n + 1);
        deviceUserIds(target).then(setAccess).catch(() => setAccess([]));
        // Firmware panel starts from the truth too.
        setBinPick(r.device.OTABinFileId != null ? String(r.device.OTABinFileId) : '');
        setPendingCmd(r.device.PendingCommand || '');
        if (!refresh) { setFwMsg(null); setOtaLines([]); }
        servedOtaUrl(target).then(setServedNow).catch(() => setServedNow(''));
      } else {
        setAccess([]);
        setServedNow('');
      }
    } catch (e) {
      setError('Lookup failed. Check the connection and try again.');
    } finally {
      setSearching(false);
    }
  }, [uno, users]);

  // Locations follow the chosen user — and the lookup, so that a second device
  // with the SAME owner still re-applies the preselect. See `preselect`.
  //
  // ⚠️ THE `alive` GUARD IS LOAD-BEARING. Two lookups in a row leave the first
  // list in flight; without it, the slower reply lands last and preselects the
  // location of a device that is no longer on screen.
  useEffect(() => {
    setLocationId('');
    setRooms([]);
    setRoomId('');
    setPreselectMiss('');
    if (!user) { setLocations([]); pending.current = null; return undefined; }
    let alive = true;
    locationsOf(user.UserId).then((list) => {
      if (!alive) return;
      setLocations(list);
      // The device's own location, once its list exists.
      const want = pending.current?.locationId;
      const wanted = want !== undefined && want !== '' && Number(want) > 0;
      if (wanted && list.some((l) => String(l.LocationID) === String(want))) {
        setLocationId(want);
        return;
      }
      // Nothing to hang a room preselect on, and the ref must not survive into a
      // later MANUAL location pick — it would preselect a room belonging to a
      // device the operator has stopped looking at.
      if (wanted) setPreselectMiss('location');
      pending.current = null;
    }).catch(() => {
      if (!alive) return;
      setLocations([]);
      pending.current = null;
    });
    return () => { alive = false; };
  }, [user, preselect]);

  // Rooms follow the chosen location — and the lookup, for the same reason.
  useEffect(() => {
    setRoomId('');
    setPreselectMiss('');
    if (!user || !locationId || locationId === NEW) { setRooms([]); return undefined; }
    let alive = true;
    roomsOf(user.UserId, locationId).then((list) => {
      if (!alive) return;
      setRooms(list);
      const want = pending.current?.roomId;
      // Room 0 is valid (common device), so this tests for undefined, not truth.
      if (want !== undefined && want !== '') {
        if (Number(want) === COMMON_ROOM_ID
          || list.some((r) => String(r.RoomID) === String(want))) {
          setRoomId(Number(want));
        } else {
          setPreselectMiss('room');
        }
      }
      pending.current = null;   // one-shot: never fight a later manual change
    }).catch(() => { if (alive) setRooms([]); });
    return () => { alive = false; };
  }, [user, locationId, preselect]);

  // ── Browse cascade ────────────────────────────────────────────────────────
  // Each level clears the ones below it, so a stale room from the previous
  // location can never be shown as belonging to the new one — and then each
  // level DEFAULTS to the first of its own list, so choosing a user selects
  // straight through to a device instead of stopping three clicks short.
  //
  // ⚠️ THE DEVICE DEFAULT RUNS THE LOOKUP. In this row picking a device IS the
  // lookup, so a user chosen here fills the whole page. That is the intent, and
  // it is also why every level below is guarded by `alive`: a second user picked
  // while the first user's lists are still in flight would otherwise land last
  // and open a device belonging to the user no longer selected.
  useEffect(() => {
    setBLocationId(''); setBRooms([]); setBRoomId(''); setBDevices([]);
    if (!bUser) { setBLocations([]); return undefined; }
    let alive = true;
    locationsOf(bUser.UserId).then((list) => {
      if (!alive) return;
      setBLocations(list);
      if (list.length) setBLocationId(list[0].LocationID);
    }).catch(() => { if (alive) setBLocations([]); });
    return () => { alive = false; };
  }, [bUser]);

  useEffect(() => {
    setBRoomId(''); setBDevices([]);
    if (!bUser || !bLocationId) { setBRooms([]); return undefined; }
    let alive = true;
    roomsOf(bUser.UserId, bLocationId).then((list) => {
      if (!alive) return;
      setBRooms(list);
      // The first REAL room. "Common devices" leads the picker's option list but
      // is not one of the location's rooms — it is the RoomId 0 bucket, and
      // defaulting to it would usually default to an empty one. It is the
      // fallback only when the location genuinely has no rooms.
      setBRoomId(list.length ? list[0].RoomID : COMMON_ROOM_ID);
    }).catch(() => { if (alive) setBRooms([]); });
    return () => { alive = false; };
  }, [bUser, bLocationId]);

  useEffect(() => {
    setBDeviceUno('');
    if (!bUser || !bLocationId || bRoomId === '') { setBDevices([]); return undefined; }
    let alive = true;
    devicesIn(bUser.UserId, bLocationId, bRoomId).then((list) => {
      if (!alive) return;
      setBDevices(list);
      // Not a highlight — this is the lookup. See the note above.
      if (list.length) pickRef.current(list[0].UniqueNumber);
    }).catch(() => { if (alive) setBDevices([]); });
    return () => { alive = false; };
  }, [bUser, bLocationId, bRoomId]);

  // Picking a device fills the box AND runs the lookup, passing the number
  // explicitly — see the note on search().
  const pickDevice = (u) => {
    if (!u) { setBDeviceUno(''); return; }
    setBDeviceUno(String(u));
    setUno(String(u));
    search(String(u));
  };

  // ⚠️ A REF, NOT A DEPENDENCY OF THE DEVICES EFFECT ABOVE. pickDevice writes
  // the number into the search box, which re-creates search() and so
  // pickDevice; as a dependency, its own auto-pick would re-trigger the effect
  // that called it, re-fetch the room and pick again.
  useEffect(() => { pickRef.current = pickDevice; });

  const canSave = useMemo(() => {
    if (!result?.found || !user) return false;
    if (!locationId) return false;
    if (locationId === NEW && !newLocation.trim()) return false;
    if (roomId === '') return false;
    if (roomId === NEW && !newRoom.trim()) return false;
    return true;
  }, [result, user, locationId, newLocation, roomId, newRoom]);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved('');
    try {
      let locId = locationId;
      if (locationId === NEW) locId = await createLocation(user.UserId, newLocation.trim());

      let rmId = roomId;
      if (roomId === NEW) rmId = await createRoom(user.UserId, locId, newRoom.trim());

      await assignDevice({
        device: result.device,
        uniqueNumber: uno.trim(),
        userId: user.UserId,
        locationId: locId,
        roomId: rmId,
        deviceName,
      });
      // Type lives on the same row but is not part of the assignment chain, so
      // it is written separately and only when it actually changed.
      // ⚠️ saveDeviceType, NOT setDeviceType — the state setter of the same
      // name shadows the import, so calling it here would silently set local
      // state and never reach the server.
      if (deviceType !== '' && String(deviceType) !== String(result.device.DeviceID)) {
        await saveDeviceType(uno.trim(), deviceType);
      }
      setSaved(`Assigned to ${userLabel(user)}.`);
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setError(e.message || 'Could not save the assignment.');
    } finally {
      setSaving(false);
    }
  };

  // ── Firmware actions ─────────────────────────────────────────────────────

  // Write DevicesInstalled.OTABinFileId, then ask the firmware's own endpoint
  // what the device will now download. The read-back is the point: everything
  // in this area fails by reporting success.
  const saveBin = async () => {
    setFwBusy(true); setFwMsg(null);
    try {
      const row = bins.find((b) => String(b.OTABinFileId) === String(binPick));
      const out = await mapDeviceToBinFile(uno.trim(), binPick, row && row.BinFileName);
      setServedNow(out.url);
      setFwMsg(out.ok
        ? { severity: 'success', text: `Confirmed — the device will download ${out.url}` }
        : {
          severity: 'warning',
          text: `Saved, but the server still resolves this device to ${out.url || '(nothing)'}.`
            + ' Do not send Update code until that is explained.',
        });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not set the firmware file.' });
    } finally {
      setFwBusy(false);
    }
  };

  // ── The silent-failure fallback ──────────────────────────────────────────
  // A publish to a device that is not connected is DROPPED, and MQTT cannot
  // report that: publishing to nobody is not an error. So the button used to
  // succeed loudly and do nothing, and the operator had to notice the absence
  // of a reply and type UPDATE CODE into the pending box themselves.
  //
  // Silence for OTA_SILENCE_MS is the missing signal. When it comes, the MQTT
  // route has definitively failed, and the poll queue is the only route left.
  const parked = useRef(false);

  const parkAfterSilence = async () => {
    const target = uno.trim();
    const already = result?.device?.PendingCommand;
    const secs = Math.round(OTA_SILENCE_MS / 1000);

    // ⚠️ ONE SLOT, NOT A QUEUE. Overwriting somebody's RESTART with our own
    // command, automatically, because a different device did not answer, is a
    // silent destructive write. Repeating an update is free; replacing an
    // instruction is not.
    if (already && !isUpdateCommand(already)) {
      setFwMsg({
        severity: 'warning',
        text: `No answer in ${secs}s — the device is not listening, so nothing was sent.`
          + ` NOT queued either: “${already}” is already parked and the slot holds only one`
          + ' command. Clear it below if the update should take its place.',
      });
      return;
    }
    try {
      await setPendingCommand(target, PENDING_UPDATE_COMMAND);
      parked.current = true;
      setPendingCmd(PENDING_UPDATE_COMMAND);
      setFwMsg({
        severity: 'warning',
        text: `No answer in ${secs}s, so ${PENDING_UPDATE_COMMAND} was parked for the device's`
          + ' next poll — about 20 minutes. ⚠️ That poll is confirmed only on the ESP32-C3'
          + ' firmware; an ESP8266 may never ask, so check with “What is it running?” later'
          + ' rather than treating this as scheduled.',
      });
      await search(target, { refresh: true });
    } catch (e) {
      setFwMsg({
        severity: 'error',
        text: `No answer in ${secs}s, and the update could not be queued either.`
          + ' The device has not been told anything — try again when it is back.',
      });
    }
  };

  // ⚠️ A LATE ANSWER MEANS THE PREMISE WAS WRONG. The queue is for a device
  // that is not listening; one that speaks at twelve seconds was listening all
  // along and is updating right now, so leaving the command parked re-flashes it
  // again twenty minutes later for nothing. Withdraw it — and only ever what we
  // ourselves parked, never something that was already there.
  const unpark = async () => {
    parked.current = false;
    try {
      await setPendingCommand(uno.trim(), '');
      setPendingCmd('');
      setFwMsg({
        severity: 'info',
        text: 'The device answered after all, so the queued update was withdrawn —'
          + ' it is updating now and does not need to do it again on its next poll.',
      });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({
        severity: 'warning',
        text: 'The device answered after all, but the queued update could not be withdrawn.'
          + ' Clear the pending message below, or it will update again on its next poll.',
      });
    }
  };

  // Publish UPDATE CODE and watch what the device says back. Reaches a device
  // that is listening NOW; the pending message below is for one that is not.
  const sendUpdate = () => {
    if (otaStop.current) otaStop.current();
    setOtaLines([]);
    parked.current = false;
    setFwMsg({ severity: 'info', text: `Published ${otaPayload(uno.trim(), userId)} — watching ${uno.trim()}/client.` });
    otaStop.current = startOtaAndWatch(uno.trim(), userId, (ev) => {
      if (parked.current) unpark();
      setOtaLines((prev) => {
        const next = [...prev, ev];
        const out = otaOutcome(next);
        if (out === 'failed') {
          setFwMsg({
            severity: 'error',
            text: 'The device refused or could not complete it. A repeated'
              + ' "OTA FAILED (download)" usually means the image is for a different chip.',
          });
        } else if (out === 'flashed' || out === 'back') {
          // ⚠️ Not success: a crash-looping image is reverted about a minute
          // after boot, having already published OTA OK.
          setFwMsg({
            severity: 'warning',
            text: 'It flashed and rebooted — but that is not proof the release changed.'
              + ' An image that crash-loops is reverted about a minute after boot,'
              + ' having already reported success. Re-check once it is back.',
          });
        }
        return next;
      });
    }, parkAfterSilence);
  };

  // ⚠️ THE ONLY CHECK THAT SETTLES IT. Comparing a version before and after
  // cannot separate a silent rollback from a device that was already on the
  // build you just flashed — observed on E072A14582D0, which reported the same
  // sha on both sides of a real update. Comparing against the FILE's sha does.
  const checkRunning = async () => {
    setFwBusy(true);
    try {
      const v = await askVersion(uno.trim(), userId);
      const row = bins.find((b) => String(b.OTABinFileId) === String(binPick));
      const want = row ? knownBuildSha(row.BinFileName) : '';
      const hit = matchesBuild(v, want);
      if (!v) {
        setFwMsg({
          severity: 'info',
          text: 'No answer. The device may be offline, or an older ESP8266 build,'
            + ' which has no VERSION command — this is not evidence of a fault.',
        });
      } else if (hit === true) {
        setFwMsg({ severity: 'success', text: `Confirmed running ${row.BinFileName} — FW ${v.version}, sha ${v.sha}.` });
      } else if (hit === false) {
        setFwMsg({
          severity: 'error',
          text: `It reports sha ${v.sha}, but ${row.BinFileName} is ${want}.`
            + ' The device is NOT running that file — either it never took, or it rolled back.',
        });
      } else {
        setFwMsg({
          severity: 'info',
          text: `Running FW ${v.version}, sha ${v.sha}, built ${v.built}.`
            + ' No published build recorded on this computer for that file, so it cannot be compared.',
        });
      }
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not ask for the version.' });
    } finally {
      setFwBusy(false);
    }
  };

  const savePending = async () => {
    setFwBusy(true); setFwMsg(null);
    try {
      await setPendingCommand(uno.trim(), pendingCmd);
      setFwMsg(pendingCmd.trim()
        ? {
          severity: 'success',
          text: `Parked "${pendingCmd.trim()}". The device runs it on its next poll,`
            + ' within about 20 minutes. Nothing confirms it was picked up.',
        }
        : { severity: 'success', text: 'Pending message cleared.' });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not save the pending message.' });
    } finally {
      setFwBusy(false);
    }
  };

  // Rename the selected room in place. Rooms are GLOBAL — the new name is what
  // everyone with access to that room sees, not just this user — so it asks
  // rather than editing inline.
  const [renaming, setRenaming] = useState(false);
  const [roomNewName, setRoomNewName] = useState('');
  const selectedRoom = rooms.find((r) => String(r.RoomID) === String(roomId));

  const doRenameRoom = async () => {
    const name = roomNewName.trim();
    if (!selectedRoom || !name) return;
    setSaving(true);
    try {
      await renameRoom(selectedRoom.RoomID, name);
      setRooms((prev) => prev.map((r) => (String(r.RoomID) === String(selectedRoom.RoomID)
        ? { ...r, RoomName: name } : r)));
      setRenaming(false);
      setSaved(`Room renamed to “${name}”.`);
    } catch (e) {
      setError('Could not rename the room.');
    } finally {
      setSaving(false);
    }
  };

  const refreshAccess = () => deviceUserIds(uno.trim()).then(setAccess).catch(() => {});

  const grant = async (id) => {
    if (!id) return;
    setAccessBusy(true);
    setError('');
    try {
      await grantUserAccess(result.device, id);
      setAddUserId('');
      await refreshAccess();
    } catch (e) {
      setError(e.message || 'Could not give that user access.');
    } finally {
      setAccessBusy(false);
    }
  };

  const revoke = async (id) => {
    setAccessBusy(true);
    setError('');
    try {
      await revokeUserAccess(result.device, id);
      await refreshAccess();
    } catch (e) {
      setError(e.message || 'Could not remove that user.');
    } finally {
      setAccessBusy(false);
    }
  };

  const doUnassign = async () => {
    // Checked against the SESSION record rather than the server: there is no
    // "verify my password" endpoint, and this is a mis-click guard, not an
    // authentication boundary — the page is already Super Admin only.
    if (!pw || String(pw) !== String(me?.Password ?? '')) {
      setPwError('That is not your password.');
      return;
    }
    setSaving(true);
    setPwError('');
    try {
      await unassignDeviceFully(result.device, uno.trim());
      setUnassignOpen(false);
      setPw('');
      setSaved('Device unassigned.');
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setPwError('Could not unassign. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">This page is for administrators.</Alert>
      </Box>
    );
  }

  const d = result?.device;
  // Compact fact list. Short labels — the values say what they are, and a
  // column of full sentences is the bulk this page had too much of.
  //
  // A third value is an optional tooltip: two of these dates say something
  // narrower than their label suggests, and that is worth one hover rather than
  // a paragraph on screen.
  const facts = d ? [
    ['Installed Id', d.ID],
    ['Type', `${d.DeviceTypeName || '—'} · ID ${d.DeviceID}`],
    ['Outputs', d.DeviceOutputs ?? d.Outputs],
    ['Owner', d.DeviceMasterUserName || d.DeviceMasterUser || '—'],
    ['Location', d.LocationName || '—'],
    ['Room', d.RoomName || (Number(d.RoomId) === COMMON_ROOM_ID ? 'Common device' : '—')],
    // The network the device is JOINED TO, reported by the firmware itself on
    // every status update — not a plan, not the location's configured SSID.
    ['Wi‑Fi', d.WifiName || '—',
      'The SSID the device reports it is connected to. Sent with every status'
      + ' update, so it is as fresh as the last thing the device said.'],
    ['IP', d.DeviceIp || '—'],
    ['Last seen', <When raw={d.LastUpdatedDateTime} />,
      'The last time anything wrote this device’s row.'],
    ['Last MQTT ping', <When raw={d.LastMqttPing} />,
      'The last time the device itself spoke to the broker.'],
    ['Code updated', <When raw={d.CodeUpdated} />,
      'When the device last STARTED an update — it stamps this before flashing,'
      + ' so an image the bootloader then rejected still sets it. “What is it'
      + ' running?” is the only thing that settles what is actually on the chip.'],
  ] : [];

  // Fields are sized to their CONTENT, not stretched to the panel. A room name
  // is a couple of words; a full-width box for it just pushes everything else
  // down the page.
  const W = { user: 230, loc: 190, room: 175, dev: 240, uno: 190, name: 220, bin: 300 };

  const typeChanged = d && deviceType !== '' && String(deviceType) !== String(d.DeviceID);
  const typeName = (types.find((t) => String(t.DeviceID) === String(deviceType)) || {}).DeviceName || '';

  // The device's owner is listed but cannot be removed here — dropping the
  // owner's own mapping would leave a device with an owner who cannot see it.
  // Unassign is the way to change ownership.
  const ownerId = d ? Number(d.MasterUserId) : null;
  const accessRows = access
    .map((id) => ({ id, u: users.find((x) => Number(x.UserId) === Number(id)) }))
    .sort((a, b) => (a.id === ownerId ? -1 : b.id === ownerId ? 1 : 0));

  return (
    // ⚠️ ONE SCREEN, NO PAGE SCROLL — and the panels scroll instead.
    //
    // This page used to be a 1040px column down the middle of a 1350px window:
    // 300px of empty gutter on either side while the firmware panel was pushed
    // below the fold. On an admin bench that means scrolling away the device's
    // facts to press a firmware button and back again to read the result of it,
    // which is exactly the pair that has to be read together.
    //
    // So: full width, a fixed-height flex column, and every panel a scroller of
    // its own (`minHeight: 0` — without it a flex child refuses to shrink below
    // its content and the page grows a scrollbar again).
    <Box sx={{
      height: '100%', p: 1, display: 'flex', flexDirection: 'column', gap: 1,
      overflow: 'hidden',
    }}
    >
      {/* No page title: the menu entry already said where you are, and a
          heading plus a subtitle cost a third of the fold for nothing. */}
      <Paper variant="outlined" sx={{ p: 1, flex: '0 0 auto' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Picker
            label="User" width={W.user} options={userOptions(users)}
            value={bUser ? bUser.UserId : ''}
            onChange={(id) => setBUser(users.find((u) => String(u.UserId) === String(id)) || null)}
          />
          <Picker
            label="Location" width={W.loc} options={locationOptions(bLocations)}
            value={bLocationId} onChange={setBLocationId} disabled={!bUser}
          />
          <Picker
            label="Room" width={W.room} disabled={!bLocationId}
            value={bRoomId} onChange={setBRoomId}
            options={[{ id: COMMON_ROOM_ID, label: 'Common devices' }, ...roomOptions(bRooms)]}
          />
          <Picker
            label="Device" width={W.dev} options={deviceOptions(bDevices)}
            value={bDeviceUno} onChange={pickDevice}
            disabled={bRoomId === '' || bDevices.length === 0}
          />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <TextField
            size="small" label="Unique Number" value={uno}
            // Typing a number by hand un-names the browse selection: the moment
            // the two differ, the Device select is describing something other
            // than what the page is about to show.
            onChange={(e) => {
              setUno(e.target.value);
              if (e.target.value !== bDeviceUno) setBDeviceUno('');
            }}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            sx={{ width: W.uno }}
          />
          <Button
            variant="contained" size="small" startIcon={<SearchIcon />}
            onClick={() => search()} disabled={searching || !uno.trim()}
            sx={{ height: 40 }}
          >
            {searching ? <CircularProgress size={18} /> : 'Find'}
          </Button>
          {result && (
            <Chip
              size="small" sx={{ mt: 1 }}
              color={!result.found ? 'error' : result.assigned ? 'success' : 'warning'}
              label={!result.found ? 'Not found'
                : result.assigned ? 'Assigned' : 'Unassigned'}
            />
          )}
          {/* Messages ride in this row rather than owning one of their own —
              a band that is empty most of the time still costs its height on
              every paint. */}
          {error && <Alert severity="error" sx={{ py: 0, flex: '1 1 260px' }}>{error}</Alert>}
          {saved && <Alert severity="success" sx={{ py: 0, flex: '1 1 260px' }}>{saved}</Alert>}
          {result && !result.found && (
            <Alert severity="info" sx={{ py: 0, flex: '1 1 260px' }}>
              Not registered. It has to be created in the manufacturing system first.
            </Alert>
          )}
        </Box>
      </Paper>

      {d && (
        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gap: 1,
          alignItems: 'stretch',
          // Three columns once there is room for them, and the widest goes to
          // Firmware — it carries the served URL and the device's own OTA log,
          // which are the only monospace lines here.
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'minmax(280px, 0.85fr) minmax(320px, 1fr) minmax(360px, 1.2fr)',
          },
          gridAutoRows: { xs: 'auto', lg: 'minmax(0, 1fr)' },
          overflow: { xs: 'auto', lg: 'hidden' },
        }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
            <Paper variant="outlined" sx={{ p: 1.25, flex: '0 0 auto' }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Device
              </Typography>
              <Table size="small" sx={{ '& td': { border: 0, py: 0.15 } }}>
                <TableBody>
                  {facts.map(([k, v, tip]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ color: 'text.secondary', width: 104, fontSize: 12 }}>
                        {tip ? (
                          <Tooltip title={tip} placement="left">
                            <Box component="span" sx={{ borderBottom: '1px dotted', cursor: 'help' }}>
                              {k}
                            </Box>
                          </Tooltip>
                        ) : k}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                        {v === null || v === undefined || v === '' ? '—' : v}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Paper
              variant="outlined"
              sx={{ p: 1.25, flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {`Users with access (${accessRows.length})`}
              </Typography>

              <Box sx={{
                display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1,
                flex: '1 1 auto', minHeight: 0, overflowY: 'auto', alignContent: 'flex-start',
              }}
              >
                {accessRows.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nobody but the owner can see this device.
                  </Typography>
                )}
                {accessRows.map(({ id, u }) => (
                  <Chip
                    key={id}
                    size="small"
                    label={u ? `${userLabel(u)} · ${u.MobileNumber || id}` : `User ${id}`}
                    color={id === ownerId ? 'primary' : 'default'}
                    variant={id === ownerId ? 'filled' : 'outlined'}
                    onDelete={id === ownerId ? undefined : () => revoke(id)}
                    disabled={accessBusy}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: '0 0 auto' }}>
                <Picker
                  label="Add user" width={W.user}
                  value={addUserId} onChange={setAddUserId}
                  options={userOptions(users.filter((u) => !access.includes(Number(u.UserId))))}
                  helperText="Also grants this location and room — without them the device stays invisible."
                />
                <Button
                  size="small" variant="outlined" startIcon={<PersonAddIcon />}
                  onClick={() => grant(addUserId)}
                  disabled={!addUserId || accessBusy} sx={{ height: 40 }}
                >
                  Give access
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Column 2: Assign, then the device's own Wi-Fi list. Stacked in one
              grid cell rather than added as a fourth column — three is already
              the widest this page lays out, and a fourth would wrap under
              Device and read as a new row of unrelated things. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
          <Paper
            variant="outlined"
            sx={{ p: 1.25, minHeight: 0, overflowY: 'auto' }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Assign
            </Typography>

            {/* The device IS somewhere — the facts table says where — but that
                somewhere is not in this user's list, so the select below is
                blank. Said out loud, because a blank select reads as "this
                device has no room" and the next thing the operator does is pick
                one, which MOVES the device. Usually a missing user->location or
                user->room mapping rather than a wrong assignment. */}
            {preselectMiss && (
              <Alert
                severity="warning"
                sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}
              >
                <Typography variant="body2">
                  {preselectMiss === 'location'
                    ? `The device is in ${d.LocationName || `location ${d.LocationID ?? d.LocationId}`}, which is not one of ${user ? userLabel(user) : 'this user'}’s locations — so nothing is preselected. Saving a different one MOVES the device.`
                    : `The device is in ${d.RoomName || `room ${d.RoomId}`}, which is not in this location’s room list for ${user ? userLabel(user) : 'this user'} — so no room is preselected. Saving a different one MOVES the device.`}
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              <TextField
                size="small" label="Device name" value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                sx={{ width: W.name }}
              />
              <Picker
                label="Master user" width={W.user} options={userOptions(users)}
                value={user ? user.UserId : ''}
                onChange={(id) => setUser(users.find((u) => String(u.UserId) === String(id)) || null)}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Picker
                label="Location" width={W.loc} value={locationId} onChange={setLocationId}
                disabled={!user}
                options={[...locationOptions(locations), { id: NEW, label: '＋ New location…' }]}
              />
              {locationId === NEW && (
                <TextField
                  size="small" label="New location" value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)} sx={{ width: W.loc }}
                />
              )}
              <Picker
                label="Room" width={W.room} value={roomId} onChange={setRoomId}
                disabled={!locationId}
                options={[
                  // Room 0 is a real assignment, not a blank — the device
                  // belongs to the location and shows under Common Devices.
                  { id: COMMON_ROOM_ID, label: 'Common device' },
                  ...roomOptions(rooms),
                  { id: NEW, label: '＋ New room…' },
                ]}
              />
              {roomId === NEW && (
                <TextField
                  size="small" label="New room" value={newRoom}
                  onChange={(e) => setNewRoom(e.target.value)} sx={{ width: W.room }}
                />
              )}
              {selectedRoom && (
                <Button
                  size="small" variant="text" sx={{ height: 40, textTransform: 'none' }}
                  onClick={() => { setRoomNewName(selectedRoom.RoomName || ''); setRenaming(true); }}
                >
                  Rename room
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Picker
                label="Device type" width={W.dev} value={deviceType}
                onChange={setDeviceType}
                // ⚠️ The TYPES table calls the type's name `DeviceName`, while
                // an installed device's row calls the same thing
                // `DeviceTypeName`. Reading DeviceTypeName here yields
                // undefined and every option falls back to "Type 49".
                options={types.map((t) => ({
                  id: t.DeviceID,
                  label: t.DeviceName || `Type ${t.DeviceID}`,
                  sub: `${t.DeviceType || ''} · ID ${t.DeviceID}`.replace(/^ · /, ''),
                }))}
              />
              {/* Changing the type usually means the name is now wrong ("4
                  TOUCH" on a device that is no longer a 4-touch). Offered, not
                  automatic: plenty of devices are deliberately named after the
                  room or the load rather than the hardware. */}
              {typeChanged && typeName && deviceName !== typeName && (
                <Button
                  size="small" variant="outlined" sx={{ height: 40, textTransform: 'none' }}
                  onClick={() => setDeviceName(typeName)}
                >
                  {`Rename to “${typeName}”`}
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
              <Button
                variant="contained" size="small" startIcon={<SaveIcon />} onClick={save}
                disabled={!canSave || saving}
              >
                {saving ? <CircularProgress size={18} /> : 'Save'}
              </Button>
              {result.assigned && (
                <Button
                  size="small" color="error" variant="outlined"
                  startIcon={<LinkOffIcon />}
                  onClick={() => { setPw(''); setPwError(''); setUnassignOpen(true); }}
                  disabled={saving}
                >
                  Unassign
                </Button>
              )}
            </Box>
          </Paper>

          {/* ── The device's OWN saved networks ─────────────────────────────
              Asked of the DEVICE over MQTT (`getallwifi`), not read from the
              database: this is the list the firmware will actually try, in the
              order it will try them, which is the only list worth seeing when a
              device will not come online. The Wi-Fi row in Device facts above is
              a different fact — the ONE network it is currently joined to.

              ⚠️ Needs the device to be online. It is a round trip with a
              timeout, and a device that is off answers nothing — the panel says
              so rather than showing an empty list, which would read as "this
              device has no networks saved". */}
          <Paper variant="outlined" sx={{ p: 1.25, minHeight: 0, overflowY: 'auto' }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Wi‑Fi networks on this device
            </Typography>
            <DeviceWifiPanel device={{ UniqueNumber: d.UniqueNumber }} />
          </Paper>
          </Box>

          {/* ── Firmware ───────────────────────────────────────────────────
              Deliberately NOT part of the Assign save above. That one needs an
              owner, a location and a room; this one needs none of them, and the
              devices that need it most have none — a bench board is unowned and
              unplaced, which is exactly this device. Tying firmware to the
              assignment would put it out of reach for its main use. */}
          <Paper
            variant="outlined"
            sx={{ p: 1.25, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Firmware
            </Typography>

            {/* What the DEVICE will fetch, from the call the firmware itself
                makes — not what any table says. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Downloads now:
              {' '}
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                {servedNow || '—'}
              </Box>
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
              <Picker
                label="OTA bin file" width={W.bin} value={binPick} onChange={setBinPick}
                options={bins.map((b) => ({ id: b.OTABinFileId, label: b.BinFileName }))}
              />
              <Button
                size="small" variant="outlined" startIcon={<SaveIcon />}
                onClick={saveBin}
                disabled={fwBusy || binPick === '' || String(binPick) === String(d.OTABinFileId ?? '')}
              >
                Set &amp; verify
              </Button>

              <Box sx={{ width: 12 }} />

              {/* Publishes UPDATE CODE now — and, if nothing answers, falls
                  back to the pending message below by itself, because a publish
                  a device is not listening for is dropped with no error. */}
              <Button
                size="small" variant="outlined" color="warning"
                startIcon={<SystemUpdateAltIcon />}
                onClick={sendUpdate} disabled={fwBusy}
                title={`Sends it now. If nothing answers within ${Math.round(OTA_SILENCE_MS / 1000)}s,`
                  + ' the update is parked for the device\'s next poll instead.'}
              >
                Upload code
              </Button>
              {/* After an update, this is what says whether it took. */}
              <Button
                size="small" variant="outlined"
                onClick={checkRunning} disabled={fwBusy}
              >
                What is it running?
              </Button>
            </Box>

            {fwMsg && (
              <Alert
                severity={fwMsg.severity} sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.75 } }}
              >
                <Typography variant="body2">{fwMsg.text}</Typography>
              </Alert>
            )}

            {/* Every line the device publishes about the update, verbatim. The
                ack is greyed: it is sent even when the update did NOT start.
                This is the one part of the page that grows without limit, so it
                is the part that takes the leftover height and scrolls. */}
            {otaLines.length > 0 && (
              <Box sx={{ mt: 1, flex: '1 1 auto', minHeight: 40, overflowY: 'auto' }}>
                {otaLines.map((e, i) => (
                  <Typography
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all',
                      opacity: e.kind === OTA_ACK ? 0.5 : 1,
                    }}
                  >
                    {e.text}
                  </Typography>
                ))}
              </Box>
            )}

            {/* ── Pending message ─────────────────────────────────────────
                The other route: the device polls the server every 20 minutes
                and runs whatever is parked here, so this reaches a device that
                is not listening now — which MQTT cannot do. */}
            <Box sx={{
              display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5,
              flex: '0 0 auto',
            }}
            >
              <TextField
                size="small" label="Pending message" value={pendingCmd}
                onChange={(e) => setPendingCmd(e.target.value)}
                sx={{ width: 260 }}
                placeholder="UPDATE CODE"
                // ⚠️ One slot, not a queue: saving replaces whatever was there,
                // and an unrecognised command fails silently at the device.
                helperText="Run on the device's next poll (~20 min). Replaces anything already pending."
              />
              <Button
                size="small" variant="outlined" startIcon={<SaveIcon />}
                onClick={savePending} disabled={fwBusy}
                sx={{ mt: -2.5 }}
              >
                Save
              </Button>
              {d.PendingCommand ? (
                <Chip
                  size="small" color="info" variant="outlined"
                  label={`pending: ${d.PendingCommand}`}
                  sx={{ mt: -2.5 }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: -2.5 }}>
                  Nothing pending.
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      <Dialog open={renaming} onClose={() => setRenaming(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Rename room</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Rooms are shared — everyone with access to this room sees the new
            name, not just this user.
          </Typography>
          <TextField
            size="small" fullWidth autoFocus label="Room name" value={roomNewName}
            onChange={(e) => setRoomNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doRenameRoom()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={doRenameRoom} disabled={saving || !roomNewName.trim()}>
            {saving ? <CircularProgress size={18} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password-guarded unassign. Spells out exactly what will be cleared —
          "are you sure" tells the operator nothing they can weigh. */}
      <Dialog open={unassignOpen} onClose={saving ? undefined : () => setUnassignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Unassign this device?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {`${d?.DeviceName || uno} will be detached from `}
            <b>{d?.DeviceMasterUserName || 'its owner'}</b>
            {`, ${d?.LocationName || 'its location'}`}
            {d?.RoomName ? ` / ${d.RoomName}` : ''}
            {'. Its owner, location and room are cleared and it disappears from that user’s app.'}
          </Typography>
          {pwError && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{pwError}</Alert>}
          <TextField
            size="small" fullWidth type="password" autoFocus
            label="Your password" value={pw}
            onChange={(e) => { setPw(e.target.value); setPwError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && doUnassign()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnassignOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doUnassign} disabled={saving || !pw}>
            {saving ? <CircularProgress size={18} /> : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
